import { UNIQUE_POSSIBILITIES } from "@/constants/constants";
import type { Household, UPSettings } from "@/types/types";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";

export type UPHeader =
  | "Spending"
  | "Spending2"
  | "Spending3"
  | "Retirement"
  | "SocialSecurity"
  | "SocialSecurity1"
  | "SocialSecurity2"
  | "Pension"
  | "Income1"
  | "Income2"
  | "YearEnd"
  | "ROR"
  | "";

export type Notes = {
  [T in Exclude<UPHeader, "">]: string;
};

type InitialState = {
  isModified: boolean;
  settings: UPSettings;
  updateCell: {
    id: string;
    value: number;
    manualChange: boolean;
    defaultValue: number;
    note: string;
    rowIndex: number;
    col: string;
    row: number;
  };
  header: UPHeader;
  headerTitles: Notes;
  household: Household | undefined | Partial<Household>;
  gridSet: any;
  modifiedOn: any;
  localVersion: boolean;
  updateHeader: UPHeader;
  indexShift: number;
  gridSnapshotAges: {
    age1: number;
    age2?: number | null;
    ageToUse: number;
  };
  showHiddenRows: boolean;
  showRestorePopup: boolean;
  reload: boolean;
};

const initialState: InitialState = {
  settings: {
    inflation: 0.035,
    cola: 0.025,
    ror: 0.07,
    person1: {
      beginAt: UNIQUE_POSSIBILITIES.MinAge,
      amount: 0,
      showAge: true,
      showSS: true,
    },
    person2: {
      beginAt: UNIQUE_POSSIBILITIES.MinAge,
      amount: 0,
      showAge: true,
      showSS: true,
    },
    colaIncome: null,
    colaIncome2: null,
    colaSS1: null,
    colaSS2: null,
    showRor: false,
    showSS: false,
  },
  updateCell: {
    id: "",
    value: 0,
    manualChange: false,
    defaultValue: 0,
    note: "",
    rowIndex: 0,
    col: "",
    row: 0,
  },
  gridSet: [],
  isModified: false,
  modifiedOn: "",
  localVersion: false,
  header: "",
  headerTitles: {
    Spending: "",
    Spending2: "",
    Spending3: "",
    Income1: "",
    Income2: "",
    Pension: "",
    Retirement: "",
    ROR: "",
    SocialSecurity: "",
    SocialSecurity1: "",
    SocialSecurity2: "",
    YearEnd: "",
  },
  updateHeader: "",
  indexShift: 0,
  gridSnapshotAges: {
    age1: 0,
    age2: null,
    ageToUse: 0,
  },
  showHiddenRows: false,
  showRestorePopup: false,
  reload: false,
  household: undefined,
};

const uqSlice = createSlice({
  name: "uq-slice",
  initialState: initialState as InitialState,
  reducers: {
    setTitles(state, action) {
      state.headerTitles = action.payload;
    },
    resetTitles(state) {
      state.headerTitles = initialState.headerTitles;
    },
    setHeader(state, action: PayloadAction<InitialState["header"]>) {
      state.header = action.payload;
    },
    setSettings(state, action: PayloadAction<InitialState["settings"]>) {
      state.settings = action.payload;
    },
    setGrid(state, action) {
      state.gridSet = action.payload;
    },
    setIsModified(state, action) {
      state.isModified = action.payload;
    },
    setLocalVersion(state, action) {
      state.localVersion = action.payload;
    },
    setModifiedOn(state, action) {
      state.modifiedOn = action.payload;
    },
    setHousehold(state, action: PayloadAction<Household | undefined | Partial<Household>>) {
      state.household = action.payload;
    },
    setUpdateData(state, action: PayloadAction<InitialState["updateCell"]>) {
      state.updateCell = action.payload;
    },
    setUpdateHeader(state, action: PayloadAction<InitialState["updateHeader"]>) {
      state.updateHeader = action.payload;
    },
    setIndexShift(state, action: PayloadAction<InitialState["indexShift"]>) {
      state.indexShift = action.payload;
    },
    setClientAgesFromGridSnapshot(state, action: PayloadAction<{ age1: number; age2?: number }>) {
      state.gridSnapshotAges.age1 = action.payload.age1;
      state.gridSnapshotAges.age2 = action.payload.age2;
      state.gridSnapshotAges.ageToUse =
        action.payload.age2 && action.payload.age2 <= action.payload.age1
          ? action.payload.age2
          : action.payload.age1;
    },
    setShowHiddenRows(state, action: PayloadAction<boolean>) {
      state.showHiddenRows = action.payload;
    },
    setShowRestorePopup(state, action: PayloadAction<boolean>) {
      state.showRestorePopup = action.payload;
    },
    setReload(state, action: PayloadAction<boolean>) {
      state.reload = action.payload;
    },
  },
});
export const uqActions = uqSlice.actions;
export default uqSlice;
